<template>
  <main v-if="isGameInitialized" id="counter_from_start" class="lanterns game">
    <section v-if="!isGameFinished">
      <div class="top_bar">
        <div class="score_section flex-row">
          <p>{{ $_translations.value.page_lantern_your_score }}</p>

          <div class="score">0</div>
        </div>

        <div class="time_counter_box flex-row">
          <GameTimer
            ref="game-timer-ref"
            :initial-value-in-seconds="appSettingsStore.appProperties.timerForLanternGame"
            :start-immediate="false"
            @time:expired="handleTimeExpired"
          />
        </div>
      </div>
    </section>
  </main>

  <BaseImage
    src="/static/images/lanterns/lantern_down.svg"
    image-class="bottom-image"
    alt="Decorative bottom image"
  />

  <BaseLoaderModal v-if="isLoaderModalOpen" />

  <MaintenanceModal
    v-else-if="isMaintenanceModalOpen"
    @redirect:homePage="handleRedirectToHomePageFromMaintenanceModal"
  />

  <BonusGameAlreadyPlayedModal v-else-if="isBonusGameAlreadyPlayedModalOpen" />

  <PostGameLanternModal
    v-else-if="isPostGameLanternModalOpen"
    :score="postGameInfo.score"
    :prize-value="postGameInfo.prizeValue"
    @close="handleClosePostGameModal"
  />
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import $ from 'jquery';
import BaseImage from '@/components/image/BaseImage.vue';
import GameTimer from '@/components/timer/GameTimer.vue';
import BaseLoaderModal from '@/components/modal/loader/BaseLoaderModal.vue';
import MaintenanceModal from '@/components/modal/MaintenanceModal.vue';
import BonusGameAlreadyPlayedModal
  from '@/views/bonusGame/components/modal/BonusGameAlreadyPlayedModal.vue';
import PostGameLanternModal from '@/views/bonusGame/components/modal/PostGameLanternModal.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useBonusPhaseStore from '@/store/modules/bonusPhase.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import useBonusGameHook from '@/hooks/useBonusGame.js';
import { generateRandomNumberInRange } from '@/helpers/common.js';
import { apiFinishBonusGame } from '@/services/game.js';
import { apiResponseTypeEnum } from '@/constants/api.js';

const mediaSize = window.matchMedia('(max-width: 600px)');
const itemImages = {
  lantern: '/static/images/lanterns/lantern_01.svg',
  lanternSelected: '/static/images/lanterns/lantern_02.svg',
  star: '/static/images/lanterns/lantern_star_01.svg',
  starSelected: '/static/images/lanterns/lantern_star_02.svg',
};

const gameTimerRef = useTemplateRef('game-timer-ref');

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();
const bonusPhaseStore = useBonusPhaseStore();

const { redirectToHomePage } = usePageNavigationHook();
const [isLoaderModalOpen, openLoaderModal, closeLoaderModal] = useModalHook();
const [isBonusGameAlreadyPlayedModalOpen, openBonusGameAlreadyPlayedModal] = useModalHook();
const [isMaintenanceModalOpen, openMaintenanceModal, closeMaintenanceModal] = useModalHook();
const [isPostGameLanternModalOpen, openPostGameLanternModal, closePostGameLanternModal] = useModalHook();
const { addLanternStylesheet, removeLanternStylesheet } = useBonusGameHook();

const score = ref(0);
const isGameInitialized = ref(false);
const isGameFinished = ref(false);
const gameIntervalId = ref(null);
const postGameInfo = ref({ score: 0, prizeValue: 0 });

const gameOver = async () => {
  try {
    isGameFinished.value = true;
    openLoaderModal({ hideManually: true });

    const successful = score.value > 0;

    const payload = {
      successful,
      achievementName: '',
      gameId: bonusPhaseStore.bonusPhaseGameConfig.gameId,
      prize: bonusPhaseStore.bonusPhaseGameConfig.prizeValue,
      gameType: 'LIGHT_THE_LANTERNS',
    };

    await apiFinishBonusGame({ payload });

    await authStore.dispatchRetrieveAuthUserLoginDetails().catch(() => {
    });

    await closeLoaderModal({ immediate: true });

    postGameInfo.value.score = score.value;
    postGameInfo.value.prizeValue = bonusPhaseStore.bonusPhaseGameConfig.prizeValue;

    openPostGameLanternModal({
      hideImmediate: true,
      afterHideCallback: redirectToHomePage,
    });
  } catch (error) {
    await closeLoaderModal({ immediate: true });

    const modalArgs = {
      showImmediate: true,
      hideImmediate: true,
      afterHideCallback: redirectToHomePage,
    };

    if (error?.responseEnum === apiResponseTypeEnum.ALREADY_PLAYED) {
      openBonusGameAlreadyPlayedModal(modalArgs);
    } else {
      openMaintenanceModal(modalArgs);
    }
  } finally {
    bonusPhaseStore.resetBonusPhaseGameConfigState();
  }
};

const removeAllDropItems = () => {
  document.querySelectorAll('.box_lanterns').forEach(el => el.remove());
};

const handleTimeExpired = () => {
  clearInterval(gameIntervalId.value);
  removeAllDropItems();

  gameOver();
};

const handleRedirectToHomePageFromMaintenanceModal = async () => {
  await closeMaintenanceModal({ immediate: true });

  redirectToHomePage();
};

const handleClosePostGameModal = async () => {
  closePostGameLanternModal({ immediate: true });

  redirectToHomePage();
};

const constructDropItem = () => {
  const size = mediaSize.matches ? generateRandomNumberInRange(70, 100) : generateRandomNumberInRange(70, 150);
  const left = generateRandomNumberInRange(0, $('.lanterns.game').width() - 50);
  const velocity = generateRandomNumberInRange(2000, 6000);

  const elDropItem = $('<img/>', {
    class: 'box_lanterns',
    src: itemImages.lantern,
    alt: '',
    draggable: false,
    style: `width:${ size }px; height:${ size }px; left:${ left }px; transition: transform ${ velocity }ms linear;`,
  });

  const isLantern = Math.round(Math.random()) === 1;
  elDropItem.data('test', isLantern);

  if (isLantern) {
    elDropItem.addClass('lantern').attr('src', itemImages.lantern);
  } else {
    elDropItem.addClass('star').attr('src', itemImages.star);
  }

  $('.lanterns.game').append(elDropItem);

  setTimeout(() => {
    elDropItem.addClass('move');
  }, generateRandomNumberInRange(0, 5000));

  elDropItem.one('transitionend webkitTransitionEnd oTransitionEnd', function () {
    $(this).remove();
  });
};

const constructDropItemList = () => {
  for (let i = 0; i < 10; i++) {
    constructDropItem();
  }
};

const registerDropItemClickListener = () => {
  $(document).on('click', '.box_lanterns', function () {
    if ($(this).data('test')) {
      score.value++;
    } else {
      score.value--;
    }

    $('.score').text(score.value);

    if (score.value < 0) {
      $('.score').css('color', '#ed3833');
    } else if (score.value > 0) {
      $('.score').css('color', '#7fff94');
    } else {
      $('.score').css('color', '#ffffff');
    }

    if ($(this).hasClass('lantern')) {
      $(this).attr('src', itemImages.lanternSelected);
    } else {
      $(this).attr('src', itemImages.starSelected);
    }

    $(this).css('pointer-events', 'none').fadeOut(2000);
  });
};

const startGame = async () => {
  await addLanternStylesheet();

  isGameInitialized.value = true;

  await nextTick();

  gameTimerRef.value?.startTimer?.();

  registerDropItemClickListener();

  constructDropItemList();

  gameIntervalId.value = setInterval(constructDropItemList, 5000);
};

onMounted(() => {
  if (appSettingsStore.appProperties.timerForLanternGame > 0) {
    startGame();
  } else {
    openMaintenanceModal({
      hideImmediate: true,
      afterHideCallback: redirectToHomePage,
    });
  }
});

onUnmounted(removeLanternStylesheet);
</script>
